"use client";

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  ChatBubbleIcon,
  CheckCircledIcon,
  Cross2Icon,
} from "@radix-ui/react-icons";

import type {
  LeadCaptureFormData,
  LeadCaptureWidgetProps,
} from "@fluid-app/react-widgets-types";
import { captureLead } from "@fluid-app/fluid";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Button,
  Card,
  cn,
  Input,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@fluid-app/ui";

import { PhoneInput } from "../components/phone-input";
import { waitForSdkReady } from "../utils/sdkInitialization";
import ShadowDOMWrapper from "../utils/ShadowDOMWrapper";
import { useFluidSettings } from "../utils/widgetSettings";

/**
 * Custom hook to detect mobile devices
 */
function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() => {
    // Initialize with a proper check on first render
    if (typeof window === "undefined") return false;

    const userAgent = navigator.userAgent.toLowerCase();
    const isMobileUserAgent =
      /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(
        userAgent,
      );
    const isTouchDevice =
      "ontouchstart" in window || navigator.maxTouchPoints > 0;
    const isSmallScreen = window.innerWidth < 768; // Tailwind's md breakpoint

    return isMobileUserAgent || (isTouchDevice && isSmallScreen);
  });

  useEffect(() => {
    // Only listen to resize events if we need to check screen size
    // Mobile user agents don't need resize listeners
    const userAgent = navigator.userAgent.toLowerCase();
    const isMobileUserAgent =
      /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(
        userAgent,
      );

    // If it's definitely a mobile user agent, no need to listen to resize
    if (isMobileUserAgent) {
      setIsMobile(true);
      return;
    }

    const checkIsMobile = () => {
      const isTouchDevice =
        "ontouchstart" in window || navigator.maxTouchPoints > 0;
      const isSmallScreen = window.innerWidth < 768; // Tailwind's md breakpoint

      const newIsMobile = isTouchDevice && isSmallScreen;

      // Only update state if the value actually changed
      setIsMobile((prevIsMobile) => {
        if (prevIsMobile !== newIsMobile) {
          return newIsMobile;
        }
        return prevIsMobile;
      });
    };

    // Use a debounced resize handler to prevent excessive re-renders
    let timeoutId: NodeJS.Timeout;
    const debouncedCheckIsMobile = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(checkIsMobile, 250); // Increased debounce time
    };

    // Check on resize with debouncing
    window.addEventListener("resize", debouncedCheckIsMobile);

    return () => {
      window.removeEventListener("resize", debouncedCheckIsMobile);
      clearTimeout(timeoutId);
    };
  }, []);

  return isMobile;
}

/**
 * Lead Capture Widget Component
 *
 * A chat-like widget for collecting user information (name, email, and message).
 * Uses Sheet component on mobile devices and Popover on desktop for better UX.
 */
export function LeadCaptureWidget({
  title = "Have a question?",
  description = "We'd love to get in touch!",
  submitButtonText = "Send Message",
  successMessage = "Thank you for your message! We'll get back to you soon.",
  onSubmit,
  show = true,
  triggerPosition = "bottom-right",
  size = 60,
  xMargin = 40,
  yMargin = 98,
  hideWidget = false,
  avatarSrc: propAvatarSrc,
  logoSrc: propLogoSrc,
  logoAlt: propLogoAlt,
  footerText = "Use is subject to terms",
  termsUrl = "https://fluid.app/terms-conditions",
  fluidShopDisplayName,
  contactMethod = "email",
  attractBubbleShowDelay = 0,
  attractBubbleHideDelay = 20000,
}: LeadCaptureWidgetProps) {
  // Check for server-side rendering early
  const isServer = typeof window === "undefined";

  // If we're on the server, render a minimal version
  if (isServer) {
    return null; // Or return a simple placeholder if needed
  }

  // Don't render if show is false - moved before hooks
  if (!show) {
    return null;
  }

  const isMobile = useIsMobile();
  const [isOpen, setIsOpen] = useState(false);
  const [allowReopen, setAllowReopen] = useState(true);

  // Memoize isMobile to prevent unnecessary re-renders
  const stableIsMobile = useMemo(() => isMobile, [isMobile]);

  const [showAttractBubble, setShowAttractBubble] = useState(false);
  const [bubbleAnimationState, setBubbleAnimationState] = useState<
    "hidden" | "showing" | "hiding"
  >("hidden");
  const [formData, setFormData] = useState<LeadCaptureFormData>({
    name: "",
    email: contactMethod === "email" ? "" : undefined,
    phone: contactMethod === "phone" ? "" : undefined,
    message: "",
  });
  const [errors, setErrors] = useState<Partial<LeadCaptureFormData>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  // Track if user has ever interacted with the widget
  const [hasEverInteracted, setHasEverInteracted] = useState(false);

  // Create a ref to store the shadow root
  const shadowRootRef = useRef<ShadowRoot | null>(null);

  // Track if component has been mounted to prevent initial animation on re-renders
  const hasMountedRef = useRef(false);
  const hasPlayedInitialAnimationRef = useRef(false);

  // Use state to trigger re-renders when initial animation completes
  const [hasPlayedInitialAnimation, setHasPlayedInitialAnimation] =
    useState(false);

  // Track bubble timer to prevent duplicate scheduling in React Strict Mode
  const bubbleTimerScheduledRef = useRef(false);

  // Use the Fluid settings hook
  const { getDisplayNames, isLoading } = useFluidSettings();

  // Control animation timing - only start when not loading
  useEffect(() => {
    // Only start animation timing when not loading
    if (isLoading) return;

    hasMountedRef.current = true;
    // Mark initial animation as played after a short delay
    const timer = setTimeout(() => {
      hasPlayedInitialAnimationRef.current = true;
      setHasPlayedInitialAnimation(true);
    }, 1000); // After the initial animation would have completed

    return () => clearTimeout(timer);
  }, [isLoading]); // Add isLoading as dependency

  // Get the display names and other settings
  // The proper priority is already handled inside getDisplayNames:
  // 1. Direct props (passed explicitly here)
  // 2. Affiliate data from settings
  // 3. Shop data from settings
  const {
    shopDisplayName,
    logoSrc: settingsLogoSrc,
    avatarSrc: settingsAvatarSrc,
    primaryColor,
    shouldUseAffiliateInitials,
    affiliateInitials,
    affiliateDisplayName,
  } = getDisplayNames(fluidShopDisplayName, propAvatarSrc, propLogoSrc);

  // Use values directly from getDisplayNames which already applies the correct priority
  const effectiveAvatarSrc = settingsAvatarSrc;
  const effectiveLogoSrc = settingsLogoSrc;
  const effectiveLogoAlt = propLogoAlt ?? shopDisplayName;
  const effectiveFluidShopDisplayName = shopDisplayName;

  // Preload images to prevent flickering when the popup opens
  useEffect(() => {
    if (effectiveAvatarSrc) {
      const img = new Image();
      img.src = effectiveAvatarSrc;
    }
    if (effectiveLogoSrc) {
      const img = new Image();
      img.src = effectiveLogoSrc;
    }
  }, [effectiveAvatarSrc, effectiveLogoSrc]);

  // Control the attract bubble visibility
  useEffect(() => {
    if (isOpen) {
      setBubbleAnimationState("hiding");
      // Reset the timer flag when widget is opened
      bubbleTimerScheduledRef.current = false;

      // Remove after animation completes
      const animationTimer = setTimeout(() => {
        setShowAttractBubble(false);
        setBubbleAnimationState("hidden");
      }, 300);

      return () => clearTimeout(animationTimer);
    }

    // Don't show attract bubble if user has already interacted
    if (hasEverInteracted) {
      return;
    }

    // Don't show attract bubble if initial animation hasn't completed yet
    if (!hasPlayedInitialAnimation) {
      return;
    }

    // Prevent duplicate scheduling in React Strict Mode
    if (bubbleTimerScheduledRef.current) {
      return;
    }

    // Mark as scheduled to prevent duplicates
    bubbleTimerScheduledRef.current = true;

    // Show the attract bubble after configured delay
    // If the initial animation just completed, show immediately, otherwise use configured delay
    const delay = attractBubbleShowDelay || 0;
    const showTimer = setTimeout(() => {
      setShowAttractBubble(true);
      setBubbleAnimationState("showing");
    }, delay);

    // Hide the attract bubble after configured time
    const hideTimer = setTimeout(
      () => {
        setBubbleAnimationState("hiding");

        // Remove after animation completes
        setTimeout(() => {
          setShowAttractBubble(false);
          setBubbleAnimationState("hidden");
          // Reset the flag after the bubble is completely hidden
          bubbleTimerScheduledRef.current = false;
        }, 300); // Animation duration
      },
      (attractBubbleShowDelay || 0) + (attractBubbleHideDelay || 20000),
    );

    return () => {
      clearTimeout(showTimer);
      clearTimeout(hideTimer);
      // Reset the flag if effect is cleaned up
      bubbleTimerScheduledRef.current = false;
    };
  }, [
    isOpen,
    hasEverInteracted,
    hasPlayedInitialAnimation,
    attractBubbleShowDelay,
    attractBubbleHideDelay,
  ]);

  // Position classes for the trigger button
  const positionClasses = {
    "top-left": "top-0 left-0",
    "top-right": "top-0 right-0",
    "bottom-left": "bottom-0 left-0",
    "bottom-right": "bottom-0 right-0",
  };

  // Get bubble position styles and classes
  const getBubbleStyles = useCallback(() => {
    const positionStyles = {
      zIndex: 30,
      maxWidth: "240px",
    };

    switch (triggerPosition) {
      case "top-left":
        return {
          ...positionStyles,
          top: `${yMargin + size + 15}px`,
          left: `${xMargin}px`,
        };
      case "top-right":
        return {
          ...positionStyles,
          top: `${yMargin + size + 15}px`,
          right: `${xMargin}px`,
        };
      case "bottom-left":
        return {
          ...positionStyles,
          bottom: `${yMargin + size + 15}px`,
          left: `${xMargin}px`,
        };
      case "bottom-right":
        return {
          ...positionStyles,
          bottom: `${yMargin + size + 15}px`,
          right: `${xMargin}px`,
        };
      default:
        return {
          ...positionStyles,
          bottom: `${yMargin + size + 15}px`,
          right: `${xMargin}px`,
        };
    }
  }, [triggerPosition, yMargin, size, xMargin]);

  // Get pointer position classes and styles for clip-path triangle
  const getPointerClasses = useCallback(() => {
    const baseClasses =
      "absolute w-2 h-2 bg-background/80 backdrop-blur-sm border border-border/40";

    switch (triggerPosition) {
      case "top-left":
        return `${baseClasses} -top-[9px] left-6 border-b-0`;
      case "top-right":
        return `${baseClasses} -top-[9px] right-6 border-b-0`;
      case "bottom-left":
        return `${baseClasses} -bottom-[9px] left-6 border-t-0`;
      case "bottom-right":
        return `${baseClasses} -bottom-[9px] right-6 border-t-0`;
      default:
        return `${baseClasses} -bottom-[9px] right-6 border-t-0`;
    }
  }, [triggerPosition]);

  // Get clip-path style for the triangle shape
  const getPointerClipPath = useCallback(() => {
    switch (triggerPosition) {
      case "top-left":
      case "top-right":
        return { clipPath: "polygon(50% 0%, 0 100%, 100% 100%)" }; // Triangle pointing up
      case "bottom-left":
      case "bottom-right":
      default:
        return { clipPath: "polygon(50% 100%, 0 0, 100% 0)" }; // Triangle pointing down
    }
  }, [triggerPosition]);

  // Handle input changes
  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const { name, value } = e.target;
      setFormData((prev) => ({ ...prev, [name]: value }));

      // Clear error for this field when user types
      if (errors[name as keyof LeadCaptureFormData]) {
        setErrors((prev) => ({ ...prev, [name]: undefined }));
      }
    },
    [],
  );

  // Validate form data
  const validateForm = (): boolean => {
    const newErrors: Partial<LeadCaptureFormData> = {};

    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
    }

    if (contactMethod === "email" && formData.email !== undefined) {
      if (!formData.email.trim()) {
        newErrors.email = "Email is required";
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        newErrors.email = "Please enter a valid email address";
      }
    } else if (contactMethod === "phone" && formData.phone !== undefined) {
      if (!formData.phone.trim()) {
        newErrors.phone = "Phone number is required";
      } else if (
        !/^[0-9+\-\s()]{10,15}$/.test(formData.phone.replace(/\s/g, ""))
      ) {
        newErrors.phone = "Please enter a valid phone number";
      }
    } else if (contactMethod === "email") {
      newErrors.email = "Email is required";
    } else {
      newErrors.phone = "Phone number is required";
    }

    if (!formData.message.trim()) {
      newErrors.message = "Message is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Wait for SDK to be ready
      await waitForSdkReady();

      // Call custom onSubmit if provided
      if (onSubmit) {
        onSubmit(formData);
      }

      // Submit the lead data to the API
      if (formData.email) {
        await captureLead({
          message: formData.message,
          contact: {
            name: formData.name,
            email: formData.email,
          },
        });
      } else if (formData.phone) {
        await captureLead({
          message: formData.message,
          contact: {
            name: formData.name,
            phone: formData.phone,
          },
        });
      }

      // Reset form and show success message
      setIsSubmitted(true);
      setFormData({
        name: "",
        email: contactMethod === "email" ? "" : undefined,
        phone: contactMethod === "phone" ? "" : undefined,
        message: "",
      });
    } catch (error) {
      console.error("Error submitting lead capture form:", error);

      // Show error in UI (you could add an error state to display this to the user)
      setErrors((prev) => ({
        ...prev,
        message:
          error instanceof Error
            ? `Error: ${error.message}`
            : "An unexpected error occurred. Please try again.",
      }));
    } finally {
      setIsSubmitting(false);
    }
  };

  // Reset the form only when opening the popover after it's been closed
  const handleOpenChange = useCallback(
    (open: boolean) => {
      // If trying to close the popover
      if (isOpen && !open) {
        setIsOpen(false);

        // Temporarily prevent reopening
        setAllowReopen(false);

        // Allow reopening after a short delay
        setTimeout(() => {
          setAllowReopen(true);
        }, 300); // Adjust timing as needed
      }
      // Only allow opening if our allowReopen flag is true
      else if (!isOpen && open && allowReopen) {
        setIsOpen(open);

        // Mark that user has interacted with the widget
        setHasEverInteracted(true);

        // Hide the attract bubble when opening the widget
        setShowAttractBubble(false);

        // Only reset if we're re-opening the widget
        if (isSubmitted) {
          // Reset form state when opening after previous submission
          setIsSubmitted(false);
          setFormData({
            name: "",
            email: contactMethod === "email" ? "" : undefined,
            phone: contactMethod === "phone" ? "" : undefined,
            message: "",
          });
          setErrors({});
        }
      }
    },
    [isOpen, allowReopen, isSubmitted, contactMethod],
  );

  // Hide the attract bubble
  const handleDismissBubble = (e: React.MouseEvent) => {
    e.stopPropagation();
    setBubbleAnimationState("hiding");

    // Remove after animation completes
    setTimeout(() => {
      setShowAttractBubble(false);
      setBubbleAnimationState("hidden");
    }, 300); // Animation duration
  };

  // Attract Bubble Component - extracted to prevent duplication
  const AttractBubble = React.useMemo(() => {
    if (!showAttractBubble) return null;

    return (
      <div
        className={`${
          hasPlayedInitialAnimationRef.current ? "" : "widget-slide-in"
        } ${
          bubbleAnimationState === "showing"
            ? "attract-bubble-animation"
            : bubbleAnimationState === "hiding"
              ? "attract-bubble-animation-reverse"
              : ""
        } fixed rounded-lg border border-border/40 bg-background/80 p-3 shadow-lg backdrop-blur-sm`}
        style={getBubbleStyles()}
        onClick={() => {
          setHasEverInteracted(true);
          setIsOpen(true);
        }}
      >
        {/* Pointer triangle */}
        <div className={getPointerClasses()} style={getPointerClipPath()}></div>

        {/* Close button */}
        <Button
          variant={"ghost"}
          onClick={handleDismissBubble}
          className="absolute right-1 top-1 rounded-full p-1 text-muted-foreground hover:text-foreground"
        >
          <Cross2Icon className="h-3 w-3" />
        </Button>

        <div className="flex items-center gap-3">
          <div className="ripple-container">
            <Avatar className="relative z-10 h-10 w-10">
              {effectiveAvatarSrc ? (
                <AvatarImage
                  src={effectiveAvatarSrc}
                  alt="Support Agent"
                  className="object-contain"
                />
              ) : (
                <AvatarFallback className="bg-primary text-sm text-white">
                  {shouldUseAffiliateInitials ? affiliateInitials : "SA"}
                </AvatarFallback>
              )}
            </Avatar>
          </div>
          <div className="flex flex-col">
            {affiliateDisplayName && (
              <p className="text-xs font-medium text-muted-foreground">
                {affiliateDisplayName}
              </p>
            )}
            <p className="text-sm font-medium text-foreground">{title}</p>
            <p className="text-xs text-muted-foreground">{description}</p>
          </div>
        </div>
      </div>
    );
  }, [
    showAttractBubble,
    bubbleAnimationState,
    getBubbleStyles,
    getPointerClasses,
    getPointerClipPath,
    handleDismissBubble,
    effectiveAvatarSrc,
    shouldUseAffiliateInitials,
    affiliateInitials,
    affiliateDisplayName,
    title,
    description,
  ]);

  // Shared form content component - memoized to prevent re-renders
  const FormContent = React.useMemo(() => {
    return (
      <>
        <div className="border-b bg-primary/5 p-4 md:p-6">
          <div className="flex flex-col items-center gap-3">
            <Avatar className="h-16 w-16 border-4 border-border/20">
              {effectiveAvatarSrc ? (
                <AvatarImage
                  src={effectiveAvatarSrc}
                  alt={
                    affiliateDisplayName
                      ? `Avatar for ${affiliateDisplayName}`
                      : "Support Agent"
                  }
                  className="object-contain"
                />
              ) : (
                <AvatarFallback className="bg-primary text-lg text-white">
                  {shouldUseAffiliateInitials ? affiliateInitials : "SA"}
                </AvatarFallback>
              )}
            </Avatar>
            <div className="text-center">
              {affiliateDisplayName && (
                <p className="mb-1 text-sm font-medium text-foreground">
                  {affiliateDisplayName}
                </p>
              )}
              <p className="text-sm text-muted-foreground">{title}</p>
            </div>
          </div>
        </div>
        <div className="p-4 md:p-6">
          {!isSubmitted ? (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="mb-4 space-y-2">
                <div className="flex justify-start">
                  <p className="w-[80%] max-w-[80%] rounded-2xl rounded-bl-sm bg-primary px-4 py-2 text-base text-primary-foreground">
                    {description}
                  </p>
                </div>
              </div>

              <div className="flex justify-end">
                <div className="w-[90%] max-w-[90%] rounded-2xl rounded-br-sm bg-primary/5 px-4 py-4 shadow-sm">
                  <div className="space-y-3">
                    <div className="space-y-2">
                      <Input
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="Your name"
                        required
                        style={{ fontSize: "16px" }} // Prevent zoom on mobile
                        className={cn(
                          "flex rounded-none border-0 border-b-2 bg-transparent font-normal text-muted-foreground shadow-none focus:border-input focus:shadow-none focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-offset-0 md:text-sm",
                          errors.name ? "border-destructive" : "border-input",
                        )}
                      />
                      {errors.name && (
                        <p className="text-xs text-destructive">
                          {errors.name}
                        </p>
                      )}
                    </div>

                    {contactMethod === "email" && (
                      <div className="space-y-2">
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleChange}
                          placeholder="your.email@example.com"
                          required
                          style={{ fontSize: "16px" }} // Prevent zoom on mobile
                          className={cn(
                            "flex rounded-none border-0 border-b-2 bg-transparent font-normal text-muted-foreground shadow-none focus:border-input focus:shadow-none focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-offset-0 md:text-sm",
                            errors.email
                              ? "border-destructive"
                              : "border-input",
                          )}
                        />
                        {errors.email && (
                          <p className="text-xs text-destructive">
                            {errors.email}
                          </p>
                        )}
                      </div>
                    )}

                    {contactMethod === "phone" && (
                      <div className="space-y-2">
                        <PhoneInput
                          id="phone"
                          name="phone"
                          value={formData.phone ?? ""}
                          onChange={(value) =>
                            handleChange({
                              target: { name: "phone", value },
                            } as React.ChangeEvent<HTMLInputElement>)
                          }
                          error={errors.phone}
                          required
                          containerRoot={shadowRootRef.current ?? undefined}
                        />
                        {errors.phone && (
                          <p className="mt-1 text-xs text-destructive">
                            {errors.phone}
                          </p>
                        )}
                      </div>
                    )}

                    <div className="space-y-2">
                      <textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        placeholder="Your message..."
                        required
                        rows={1}
                        style={{
                          resize: "none",
                          overflow: "hidden",
                          height: "auto",
                          minHeight: "2.5rem",
                          fontSize: "16px", // Prevent zoom on mobile
                        }}
                        onInput={(e) => {
                          const target = e.target as HTMLTextAreaElement;
                          target.style.height = "auto";
                          target.style.height = `${target.scrollHeight}px`;
                        }}
                        className={cn(
                          "flex w-full rounded-none border-0 border-b-2 bg-transparent px-3 py-2 text-sm font-normal text-muted-foreground shadow-none ring-offset-background placeholder:text-muted-foreground focus:border-input focus:shadow-none focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-offset-0 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
                          errors.message
                            ? "border-destructive"
                            : "border-input",
                        )}
                      />
                      {errors.message && (
                        <p className="text-xs text-destructive">
                          {errors.message}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <div className="mt-4 flex flex-col items-center">
                <p className="mb-2 px-4 text-center text-[10px] text-muted-foreground">
                  By submitting, you authorize {effectiveFluidShopDisplayName}{" "}
                  to send{" "}
                  {contactMethod === "phone" ? "text messages" : "emails"} with
                  offers & other information, possibly using automated
                  technology, to the{" "}
                  {contactMethod === "phone" ? "number" : "email"} you provided.{" "}
                  {contactMethod === "phone" && "Message/data rates apply."}{" "}
                  Consent is not a condition of purchase.
                </p>
                <Button
                  type="submit"
                  className="rounded-full bg-primary px-5"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Sending..." : submitButtonText}
                </Button>
              </div>
            </form>
          ) : (
            <div className="space-y-4 py-6 text-center">
              <div className="flex justify-center">
                <CheckCircledIcon className="h-12 w-12 text-primary" />
              </div>
              <div className="flex justify-start">
                <p className="w-[80%] max-w-[80%] rounded-2xl rounded-bl-sm bg-primary px-4 py-2 text-base text-primary-foreground">
                  {successMessage}
                </p>
              </div>
            </div>
          )}
        </div>
        <div className="border-t border-muted p-3">
          <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground">
            {effectiveLogoSrc && (
              <img
                src={effectiveLogoSrc}
                alt={effectiveLogoAlt}
                className="h-4 max-h-4 w-auto object-contain"
              />
            )}
            <a
              href={termsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="hover:underline"
            >
              {footerText}
            </a>
          </div>
        </div>
      </>
    );
  }, [
    effectiveAvatarSrc,
    affiliateDisplayName,
    shouldUseAffiliateInitials,
    affiliateInitials,
    title,
    isSubmitted,
    handleSubmit,
    description,
    formData.name,
    formData.email,
    formData.phone,
    formData.message,
    handleChange,
    errors.name,
    errors.email,
    errors.phone,
    errors.message,
    contactMethod,
    effectiveFluidShopDisplayName,
    isSubmitting,
    submitButtonText,
    successMessage,
    effectiveLogoSrc,
    effectiveLogoAlt,
    footerText,
    termsUrl,
  ]);

  return (
    <ShadowDOMWrapper shadowRootRef={shadowRootRef} primaryColor={primaryColor}>
      {/* Don't render content until settings are loaded to prevent flicker */}
      {!isLoading && (
        <>
          {/* Attract Bubble - rendered once outside conditional logic */}
          {!hideWidget && AttractBubble}

          {stableIsMobile ? (
            // Mobile: Use Sheet
            <Sheet open={isOpen} onOpenChange={handleOpenChange}>
              {!hideWidget && (
                <>
                  <SheetTrigger
                    className={`fixed border-none ${positionClasses[triggerPosition]} ${
                      hasPlayedInitialAnimationRef.current
                        ? ""
                        : "widget-slide-in"
                    } flex cursor-pointer items-center justify-center rounded-full bg-primary text-primary-foreground shadow-md transition-transform duration-200`}
                    style={{
                      zIndex: 30,
                      width: `${size}px`,
                      height: `${size}px`,
                      margin: `${yMargin}px ${xMargin}px`,
                    }}
                    aria-label="Open chat"
                  >
                    <div className="relative flex items-center justify-center">
                      {isOpen ? (
                        <Cross2Icon className="h-6 w-6" />
                      ) : (
                        <ChatBubbleIcon className="h-6 w-6" />
                      )}
                    </div>
                  </SheetTrigger>
                </>
              )}

              <SheetContent
                side="bottom"
                className="h-[90vh] max-h-[90vh] overflow-y-auto rounded-t-lg border-0 p-0 shadow-none"
                containerRoot={shadowRootRef.current ?? undefined}
              >
                <div className="flex h-full flex-col">
                  <SheetHeader className="sr-only">
                    <SheetTitle>{title}</SheetTitle>
                  </SheetHeader>
                  <Card className="flex-1 border-0 shadow-none">
                    {FormContent}
                  </Card>
                </div>
              </SheetContent>
            </Sheet>
          ) : (
            // Desktop: Use Popover
            <Popover open={isOpen} onOpenChange={handleOpenChange}>
              {!hideWidget && (
                <>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      aria-label="Open chat"
                      className={`fixed border-none ${positionClasses[triggerPosition]} ${
                        hasPlayedInitialAnimationRef.current
                          ? ""
                          : "widget-slide-in"
                      } flex cursor-pointer items-center justify-center rounded-full bg-primary text-primary-foreground shadow-md transition-transform duration-200`}
                      style={{
                        zIndex: 30,
                        width: `${size}px`,
                        height: `${size}px`,
                        margin: `${yMargin}px ${xMargin}px`,
                      }}
                    >
                      <div className="relative flex items-center justify-center">
                        {isOpen ? (
                          <Cross2Icon className="h-6 w-6" />
                        ) : (
                          <ChatBubbleIcon className="h-6 w-6" />
                        )}
                      </div>
                    </button>
                  </PopoverTrigger>
                </>
              )}

              <PopoverContent
                className="w-80 p-0 shadow-lg sm:w-96"
                side="bottom"
                align="end"
                sideOffset={8}
                containerRoot={shadowRootRef.current ?? undefined}
              >
                <Card className="border-0 shadow-none">{FormContent}</Card>
              </PopoverContent>
            </Popover>
          )}
        </>
      )}
    </ShadowDOMWrapper>
  );
}

/**
 * Preload lead capture widget styles
 * This can be called early in the application to ensure styles
 * are ready when components are mounted
 */
export function preloadLeadCaptureWidgetStyles(): void {
  if (typeof window === "undefined") return;

  interface FluidWindow extends Window {
    __FLUID_STYLE_URL?: string;
  }

  // Access window safely after the check
  const fluidWindow = window as unknown as FluidWindow;

  // Make sure we have a style URL set
  fluidWindow.__FLUID_STYLE_URL ??= "/dist/styles.css";

  // Import shadow DOM style utilities and preload styles
  try {
    import("../utils/shadowDomStyles")
      .then((module) => {
        // Create a placeholder shadow root to initialize styles
        const tempDiv = document.createElement("div");
        const tempShadow = tempDiv.attachShadow({ mode: "open" });

        // Apply styles to initialize caching
        module.applyStylesIntoShadowRoot(tempShadow);
      })
      .catch((err) => {
        console.warn("Failed to preload lead capture widget styles:", err);
      });
  } catch (error) {
    console.warn("Could not import shadow DOM style utilities:", error);
  }
}

// Preload styles on module load (client-side only)
if (typeof window !== "undefined") {
  // Use setTimeout to ensure this runs after hydration
  setTimeout(preloadLeadCaptureWidgetStyles, 0);
}
